import React, { useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { FaChartBar, FaChartLine, FaDownload } from "react-icons/fa";
import { RiResetLeftLine } from "react-icons/ri";
import { GoNumber } from "react-icons/go";

const hourKeys = [
  "wh_less_than_equals_4hr",
  "wh_4_to_6_hr",
  "wh_6_to_8_hr",
  "wh_8_to_10_hr",
  "wh_10_to_12_hr",
  "wh_12_to_14_hr",
  "wh_more_than_14hr",
];
const HOUR_COLORS = {
  wh_less_than_equals_4hr: "#3b85e6ff",
  wh_4_to_6_hr: "#30cce0ff",
  wh_6_to_8_hr: "#278d8dff",
  wh_8_to_10_hr: "#35bd6eff",
  wh_10_to_12_hr: "#e98c00ff",
  wh_12_to_14_hr: "#ee6628ff",
  wh_more_than_14hr: "#be1414ff",
};

const mergeHourValues = (row) => {
  return {
    ...row,
    wh_8_to_10_hr:
      (Number(row.wh_8_to_9_hr) || 0) + (Number(row.wh_9_to10_hr) || 0),

    wh_10_to_12_hr:
      (Number(row.wh_10_to_11_hr) || 0) + (Number(row.wh_11_to_12_hr) || 0),

    wh_more_than_14hr: Number(row.wh_more_than_14hr) || 0,
    wh_less_than_equals_4hr: Number(row.wh_less_than_equals_4hr) || 0,
  };
};
const prettyLabel = (key) => {
  const map = {
    wh_less_than_equals_4hr: "≤4 hrs",
    wh_8_to_10_hr: "8–10 hrs",
    wh_10_to_12_hr: "10–12 hrs",
    wh_more_than_14hr: "≥14 hrs",
  };

  if (map[key]) return map[key];

  return key.replace("wh_", "").replace(/_/g, " ").replace("to", "–");
};

const StackedBarVariation = ({ data, title, singleValue = false }) => {
  const [chartType, setChartType] = useState("bar");
  const [showValues, setShowValues] = useState(false);
  const chartRef = useRef(null);

  if (!data || !data.length) return null;

  // --------------- Filter & Group ---------------
  const filteredData = data
    .filter((d) => d.wh_lobby && d.wh_lobby.toUpperCase() !== "TOTAL")
    .map(mergeHourValues);

  const grouped = {};
  filteredData.forEach((row) => {
    const lobby = row.wh_lobby;
    if (!grouped[lobby]) {
      grouped[lobby] = { wh_lobby: lobby };
      hourKeys.forEach((k) => (grouped[lobby][k] = 0));
    }
    hourKeys.forEach((k) => {
      grouped[lobby][k] += Number(row[k]) || 0;
    });
  });

  const groupedData = Object.values(grouped);
  const lobbies = groupedData.map((d) => d.wh_lobby);

  // TOTAL VALUE PER BAR
  const barTotals = groupedData.map((row) =>
    hourKeys.reduce((sum, k) => sum + (Number(row[k]) || 0), 0)
  );

  // HIGHEST TOTAL BAR VALUE
  const computedMax = Math.max(...barTotals);

  // using whole-bar totals
  const SIZE = (v, row) => {
    const total = hourKeys.reduce((s, k) => s + (Number(row[k]) || 0), 0);

    if (total < computedMax * 0.2) return "small"; // <20 %
    if (total < computedMax * 1) return "medium"; // <100 %
    return "medium";
  };

  // --------------- Stacked series ---------------
  const stackedSeries = hourKeys.map((key) => {
    const seriesData = groupedData.map((row, idx) => {
      const value = Number(row[key]) || 0;
      const sz = SIZE(value, row);

      const showInsideLabel = showValues && sz === "medium";

      return {
        value,
        label: {
          show: showInsideLabel,
          position: "inside",
          formatter: () => (showInsideLabel ? value : ""),
          color: "#363636ff", // inside value color
          fontSize: 9,
        },
      };
    });

    return {
      name: prettyLabel(key),
      type: chartType === "line" ? "line" : "bar",
      stack: chartType === "line" ? undefined : "total",
      smooth: chartType === "line",
      barWidth: "60%",
      emphasis: { focus: "series" },
      data: seriesData,
    };
  });

  const richHourStyles = {};
  hourKeys.forEach((key) => {
    richHourStyles[key] = {
      color: HOUR_COLORS[key] || "#000",
      fontSize: 10.5,
      lineHeight: 18,
    };
  });

  // ---- Overlay for SMALL segment permanent tooltip ----
  const overlayData = lobbies.map((_, idx) => {
    const smallSegments = hourKeys.filter(
      (k) => SIZE(groupedData[idx][k], groupedData[idx]) === "small"
    );

    const showOverlayLabel = showValues && smallSegments.length > 0;

    if (!showOverlayLabel) return { value: 0, label: { show: false } };

    const smallTotal = smallSegments.reduce(
      (sum, k) => sum + (groupedData[idx][k] || 0),
      0
    );

    const overlayBg = "#e2e2e2b6";

    const formatOverlayTooltip = () =>
      smallSegments
        .map(
          (key) => `{${key}|${prettyLabel(key)}: ${groupedData[idx][key] || 0}}`
        )
        .join("\n");

    return {
      value: smallTotal,
      label: {
        show: true,
        position: "top",
        formatter: () => formatOverlayTooltip(),
        backgroundColor: overlayBg,
        padding: [10, 0],
        borderRadius: 6,
        width: 110,
        align: "center",
        fontSize: 10.5,
        rich: richHourStyles,
        fontWeight: "bold",
      },
      itemStyle: { color: "transparent" },
    };
  });

  const labelSeries = {
    name: "Total",
    type: "bar",
    barGap: "-100%",
    barWidth: "60%",
    itemStyle: { color: "transparent" },
    emphasis: { disabled: true, fontWeight: "bold" },
    data: overlayData,
    z: 10,
  };

  // Chart Options
  const option = {
    title: {
      left: "center",
      text: title || "",
      textStyle: { fontSize: 16, fontWeight: "600" },
    },

    tooltip: showValues
      ? { show: true }
      : {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          backgroundColor: "#ffffffee",
        },

    legend: {
      top: 10,
      type: "scroll",
      icon: "roundRect",
    },

    grid: {
      top: "20%",
      left: "5%",
      right: "5%",
      bottom: "2%",
      containLabel: true,
    },

    xAxis: {
      type: "category",
      data: lobbies,
      axisLabel: { rotate: 40 },
    },

    yAxis: {
      type: "value",
      name: "Crew Count / Hours",
      splitArea: {
        show: true,
        areaStyle: {
          color: ["rgba(245,245,245,0.6)", "rgba(255,255,255,0.6)"],
        },
      },
    },

    color: [
      "#5f99f7ff",
      "#c5a3f5ff",
      "#ff75b1ff",
      "#fca279ff",
      "#FFBE0B",
      "#268acc9f",
      "#7ec98aff",
      "#ffa3ffff",
    ],
    series: [...stackedSeries, labelSeries],
  };

  // --------------- Export as PNG ---------------
  const downloadChart = () => {
    const chart = chartRef.current.getEchartsInstance();
    const img = chart.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: "#fff",
    });

    const a = document.createElement("a");
    a.href = img;
    a.download = `${title || "chart"}.png`;
    a.click();
  };

  // --------------- Render ---------------
  return (
    <div>
      {!singleValue && (
        <div className="d-flex justify-content-end mt-1">
          <button
            className={`btn btn-sm btn-outline-dark d-flex align-items-center me-1 ${
              showValues ? "active" : ""
            }`}
            style={{ height: "25px" }}
            onClick={() => setShowValues((s) => !s)}
          >
            <GoNumber /> {showValues ? "Values" : "Values"}
          </button>

          <button
            className="btn btn-sm btn-outline-dark d-flex align-items-center me-1"
            style={{ height: "25px" }}
            onClick={downloadChart}
          >
            <FaDownload />
          </button>

          <button
            className={`btn btn-sm btn-outline-dark d-flex align-items-center me-1 ${
              chartType === "bar" ? "active" : ""
            }`}
            style={{ height: "25px" }}
            onClick={() => setChartType("bar")}
          >
            <FaChartBar />
          </button>

          <button
            className={`btn btn-sm btn-outline-dark d-flex align-items-center me-1 ${
              chartType === "line" ? "active" : ""
            }`}
            style={{ height: "25px" }}
            onClick={() => setChartType("line")}
          >
            <FaChartLine />
          </button>

          <button
            className="btn btn-sm btn-outline-dark d-flex align-items-center me-1"
            style={{ height: "25px" }}
            onClick={() => {
              setChartType("bar");
              setShowValues(false);
            }}
          >
            <RiResetLeftLine />
          </button>
        </div>
      )}

      <ReactECharts
        ref={chartRef}
        option={option}
        notMerge={true}
        style={{ height: singleValue ? "250px" : "500px", width: "100%" }}
      />
    </div>
  );
};

export default StackedBarVariation;
